import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, CheckCircle, Loader, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import UnifiedHeader from '../../components/UnifiedHeader';
import { uploadSyllabus, createCourse, getCourse } from '../../services/studioApi';
import './Studio.css';

const STEPS = ['uploading', 'parsing', 'embedding', 'extracting', 'ready'];
const STEP_LABELS = {
  uploading:  { fi: 'Ladataan PDF...', en: 'Uploading PDF...', sv: 'Laddar upp PDF...' },
  parsing:    { fi: 'Luetaan PDF...', en: 'Reading PDF...', sv: 'Läser PDF...' },
  embedding:  { fi: 'Luodaan vektoreita...', en: 'Creating embeddings...', sv: 'Skapar inbäddningar...' },
  extracting: { fi: 'Tunnistetaan käsitteitä...', en: 'Extracting concepts...', sv: 'Extraherar koncept...' },
  ready:      { fi: 'Valmis!', en: 'Ready!', sv: 'Klar!' },
};

export default function UploadSyllabus() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};
  const tUpload = t.upload || {};
  const fileRef = useRef();

  const [title, setTitle] = useState('');
  const [lang, setLang] = useState(language || 'fi');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [error, setError] = useState('');

  const handleFile = (f) => {
    if (f && f.type === 'application/pdf') setFile(f);
    else setError(tUpload.pdfOnly || 'Please upload a PDF file');
  };

  const pollUntilReady = async (id) => {
    setCurrentStep('embedding');
    for (let i = 0; i < 60; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const course = await getCourse(id);
        if (course.status === 'ready') {
          setCurrentStep('ready');
          await new Promise(r => setTimeout(r, 1000));
          navigate(`/studio/teacher/courses/${id}`);
          return;
        }
        if (course.status === 'error') {
          throw new Error('Processing failed');
        }
      } catch (e) {
        if (e.message === 'Processing failed') { setError(tUpload.processingFailed || 'PDF processing failed. Try uploading again.'); setUploading(false); return; }
      }
    }
    setError(tUpload.timeout || 'Processing timed out. Please try again.');
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError(tUpload.selectFile || 'Please select a PDF file'); return; }
    if (!title.trim()) { setError(tUpload.enterTitle || 'Please enter a course title'); return; }

    setError('');
    setUploading(true);

    try {
      setCurrentStep('uploading');
      const { public_url } = await uploadSyllabus(file);

      setCurrentStep('parsing');
      const { course_id } = await createCourse({
        title: title.trim(),
        description: description.trim() || null,
        language: lang,
        syllabus_filename: file.name,
        syllabus_r2_url: public_url,
      });
      setCurrentStep('extracting');
      await pollUntilReady(course_id);
    } catch (e) {
      setError(e.message || 'Upload failed');
      setUploading(false);
    }
  };

  return (
    <div className="studio-page">
      <UnifiedHeader subRow={
        <div className="studio-subheader-left">
          <button className="studio-back-btn" onClick={() => navigate('/studio/teacher')}>
            <ArrowLeft size={15} /> {t.back || 'Back'}
          </button>
          <h1 className="studio-page-title">{tUpload.title || 'Upload Syllabus'}</h1>
        </div>
      } />

      <div className="studio-main" style={{ maxWidth: 580 }}>
        {!uploading ? (
          <form onSubmit={handleSubmit}>
            <div className="studio-card" style={{ marginBottom: '1.25rem' }}>
              <div className="form-group">
                <label className="form-label">{tUpload.courseTitle || 'Course Title'} *</label>
                <input
                  className="form-input"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder={tUpload.courseTitlePlaceholder || 'e.g. Chemistry Fundamentals'}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">{tUpload.description || 'Description'}</label>
                <textarea
                  className="form-textarea"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder={tUpload.descriptionPlaceholder || 'Optional course description'}
                  rows={2}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{tUpload.language || 'Course Language'}</label>
                <select className="form-select" value={lang} onChange={e => setLang(e.target.value)}>
                  <option value="fi">Suomi</option>
                  <option value="sv">Svenska</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>

            {/* Drop zone */}
            <div
              className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
              style={{ marginBottom: '1.25rem' }}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; handleFile(f); }}
              onClick={() => fileRef.current.click()}
            >
              <div className="upload-zone-icon">
                {file ? <FileText size={40} color="#667eea" /> : <Upload size={40} />}
              </div>
              {file ? (
                <>
                  <h3 style={{ color: '#667eea' }}>{file.name}</h3>
                  <p>{(file.size / 1024 / 1024).toFixed(1)} MB</p>
                </>
              ) : (
                <>
                  <h3>{tUpload.dropZoneTitle || 'Drag & drop your syllabus PDF'}</h3>
                  <p>{tUpload.dropZoneSubtitle || 'or click to browse — PDF only, max 20 MB'}</p>
                </>
              )}
              <input ref={fileRef} type="file" accept=".pdf" hidden onChange={e => handleFile(e.target.files[0])} />
            </div>

            {error && (
              <div style={{ background: '#fce4ec', color: '#c62828', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.88rem' }}>
                {error}
              </div>
            )}

            <button className="btn-primary" type="submit" style={{ width: '100%', justifyContent: 'center' }}>
              <Upload size={16} />
              {tUpload.startProcessing || 'Upload & Process Syllabus'}
            </button>
          </form>
        ) : (
          <div className="studio-card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <div className="spinner" style={{ marginBottom: '2rem' }} />
            <h2 style={{ margin: '0 0 2rem', color: '#1a1a2e' }}>
              {tUpload.processing || 'Processing your syllabus...'}
            </h2>
            {STEPS.map((step, i) => {
              const done = STEPS.indexOf(currentStep) > i;
              const active = currentStep === step;
              return (
                <div key={step} className={`process-step ${done ? 'done' : active ? 'active' : 'pending'}`}>
                  {done ? <CheckCircle size={18} /> : active ? <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #ddd' }} />}
                  {STEP_LABELS[step][language] || STEP_LABELS[step].en}
                </div>
              );
            })}
            <p style={{ marginTop: '1.5rem', color: '#aaa', fontSize: '0.85rem' }}>
              {tUpload.processingNote || 'This may take 1–3 minutes depending on PDF size'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
